import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import dayjs from "dayjs";
import { CalendarIcon } from "lucide-react";

interface Props {
	value: Date | null;
	onChange: (date: Date | null) => void;
	label?: string;
	description?: string;
	disabled?: boolean;
}

const DateInput = ({
	value,
	onChange,
	label,
	description,
	disabled,
}: Props) => {
	return (
		<div className="flex flex-col w-full h-fit text-sm">
			{label && <label className="mb-1 font-medium">{label}</label>}
			{description && (
				<p className="mb-2 text-gray-500 text-sm">{description}</p>
			)}
			<Popover>
				<PopoverTrigger
					data-testid="date-input-trigger"
					className="bg-white hover:bg-white border-slate-300 rounded-md h-12"
					asChild
				>
					<Button
						variant={"outline"}
						className={cn(
							"pl-3 pr-6 text-left text-sm font-normal text-slate-800",
						)}
					>
						{value ? (
							format(value, "PPP")
						) : (
							<span className="text-slate-600 text-sm">Pick a date</span>
						)}
						<CalendarIcon className="stroke-slate-800 ml-auto w-4 h-4" />
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className="flex justify-center items-center p-0 !min-w-sm"
					align="start"
				>
					<Calendar
						mode="single"
						selected={value}
						onSelect={(date) => {
							if (date) {
								// Set the selected date to 12 PM before updating the value
								const dateWithNoon = dayjs(date)
									.set("hour", 12)
									.set("minute", 0)
									.toDate();
								onChange(dateWithNoon);
							} else {
								onChange(null);
							}
						}}
						disabled={disabled ?? false}
						initialFocus
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export default DateInput;
